import {
  Button,
  Flex,
  Grid,
  Label,
  Loader,
  ScrollView,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";
import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";

import {
  deviceVariablesByDeviceID,
  getDevice,
  listVariables,
} from "graphql/queries";
import { globalStrings } from "config/translations/es";
import { updateDevice } from "graphql/mutations";

const DeviceCalibration = ({ deviceID, visible, onClose }) => {
  const [device, setDevice] = useState({});
  const [calibration, setCalibration] = useState([]);
  const [selectedCalibration, setSelectedCalibration] = useState({});
  const [variable, setVariable] = useState("");
  const [variables, setVariables] = useState([]);
  const [variablesLoading, setVariablesLoading] = useState(false);

  const addInput = () => {
    setSelectedCalibration((prev) => {
      let start = 0;
      let end = 1;
      if (prev.values?.length > 0) {
        const lastVal = prev.values[prev.values.length - 1];

        if (lastVal.end < lastVal.start) {
          toast.error(strings.responses.endMustBeMajor);
          return prev;
        }

        start = Number(lastVal.end || lastVal.start) + 1;
        end = start + 1;
      }
      return {
        ...prev,
        values: [
          ...prev.values,
          {
            start,
            end,
            factor: 0,
          },
        ],
      };
    });
  };

  const removeInput = (index) => {
    const listUpdated = [...selectedCalibration.values];
    listUpdated.splice(index, 1);
    setSelectedCalibration((prev) => ({
      ...prev,
      values: listUpdated,
    }));
  };

  const onChangeInput = (index, name, value) => {
    const listUpdated = [...selectedCalibration.values];

    listUpdated[index] = {
      ...listUpdated[index],
      [name]: Number(value),
    };
    setSelectedCalibration((prev) => ({
      ...prev,
      values: listUpdated,
    }));
  };

  const onChangeVariable = (value) => {
    setVariable(value);
    if (value)
      setSelectedCalibration(
        calibration?.find((cal) => cal.variable === value) ?? {
          variable: value,
          values: [
            {
              start: 0,
              end: 1,
              factor: 0,
            },
          ],
        }
      );
    else setSelectedCalibration({});
  };

  const onSave = async () => {
    if (selectedCalibration.values?.length > 0) {
      const lastVal =
        selectedCalibration.values[selectedCalibration.values.length - 1];

      if (lastVal.end < lastVal.start) {
        toast.error(strings.responses.endMustBeMajor);
        return;
      }
    }

    let calibrationUpdated = [];

    if (calibration?.length > 0) {
      if (
        calibration.some((cal) => cal.variable === selectedCalibration.variable)
      )
        calibrationUpdated = calibration.map((cal) =>
          cal.variable === selectedCalibration.variable
            ? selectedCalibration
            : cal
        );
      else calibrationUpdated = [...calibration, selectedCalibration];
    } else calibrationUpdated = [selectedCalibration];

    try {
      await API.graphql(
        graphqlOperation(updateDevice, {
          input: {
            id: deviceID,
            calibration: JSON.stringify(calibrationUpdated),
          },
        })
      );
      toast.success(strings.responses.successUpdated);
      setCalibration(calibrationUpdated);
    } catch (err) {
      toast.error(strings.responses.errorSave);
    }
  };

  async function fetchDevice(id) {
    try {
      const deviceData = await API.graphql(
        graphqlOperation(getDevice, {
          id,
        })
      );
      const deviceItem = deviceData.data.getDevice;
      setDevice(deviceItem);
      setCalibration(JSON.parse(deviceItem.calibration ?? "[]"));
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchVariables() {
    try {
      setVariablesLoading(true);
      const variablesData = await API.graphql(
        graphqlOperation(listVariables, {
          filter: {
            derived: { eq: false },
          },
        })
      );
      const variablesItems = variablesData.data.listVariables.items ?? [];

      const deviceVariablesData = await API.graphql(
        graphqlOperation(deviceVariablesByDeviceID, {
          deviceID,
        })
      );
      const deviceVariablesItems =
        deviceVariablesData.data.deviceVariablesByDeviceID.items ?? [];

      setVariables(
        variablesItems.filter((variable) =>
          deviceVariablesItems.some(
            (devVar) => devVar.variableID === variable.id
          )
        )
      );
      setVariablesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchVariables);
      setVariablesLoading(false);
    }
  }

  useEffect(() => {
    fetchDevice(deviceID);
    fetchVariables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceID]);

  return (
    <CModal
      size="xl"
      alignment="center"
      visible={visible}
      onClose={onClose}
      backdrop="static"
    >
      <CModalHeader>
        <CModalTitle>
          {strings.title} - {device.name}
        </CModalTitle>
      </CModalHeader>
      <CModalBody style={{ overflow: "hidden" }}>
        <ScrollView maxHeight="75vh">
          <div className="p-3">
            {!!variablesLoading ? (
              <Loader />
            ) : (
              <Grid
                rowGap="15px"
                columnGap="15px"
                alignItems="center"
                templateColumns={{ base: "1fr", large: "7fr 7fr 7fr 1fr" }}
              >
                <SelectField
                  label={strings.fields.variable}
                  value={variable}
                  onChange={(event) => onChangeVariable(event.target.value)}
                >
                  <option value="">{globalStrings.selectOption}</option>
                  {variables?.map((variable) => (
                    <option value={variable.shortName}>{variable.name}</option>
                  ))}
                </SelectField>
                {variable && (
                  <div style={styles.iconAction} onClick={addInput}>
                    <AddCircleOutline color="action" />
                  </div>
                )}
              </Grid>
            )}
            {selectedCalibration?.values?.map((val, index) => (
              <Grid
                key={index}
                templateColumns={{ base: "1fr", large: "7fr 7fr 7fr 1fr" }}
                rowGap="15px"
                columnGap="15px"
                alignItems="center"
                className="mt-3"
              >
                <Grid>
                  <Label>{strings.fields.start}</Label>
                  <Flex gap="5px" alignItems="center">
                    <TextField
                      width="100%"
                      type="number"
                      value={val.start ?? ""}
                      min={0}
                      onChange={(event) =>
                        onChangeInput(index, "start", event.target.value)
                      }
                      disabled
                    ></TextField>
                  </Flex>
                </Grid>
                <Grid>
                  <Label>{strings.fields.end}</Label>
                  <Flex gap="5px" alignItems="center">
                    <TextField
                      width="100%"
                      type="number"
                      value={val.end ?? ""}
                      min={0}
                      onChange={(event) =>
                        onChangeInput(index, "end", event.target.value)
                      }
                      disabled={index < selectedCalibration.values.length - 1}
                    ></TextField>
                  </Flex>
                </Grid>
                <Grid>
                  <Label>{strings.fields.factor}</Label>
                  <Flex gap="5px" alignItems="center">
                    <TextField
                      width="100%"
                      type="number"
                      value={val.factor ?? ""}
                      min={0}
                      onChange={(event) =>
                        onChangeInput(index, "factor", event.target.value)
                      }
                    ></TextField>
                  </Flex>
                </Grid>
                {index === selectedCalibration.values.length - 1 && (
                  <Flex>
                    <div
                      style={styles.iconAction}
                      onClick={() => removeInput(index)}
                    >
                      <DeleteOutline color="error" />
                    </div>
                  </Flex>
                )}
              </Grid>
            ))}
            {variable && (
              <Flex justifyContent="center" className="mt-3">
                <Button
                  children={strings.save}
                  onClick={onSave}
                  variation="primary"
                ></Button>
              </Flex>
            )}
          </div>
        </ScrollView>
      </CModalBody>
    </CModal>
  );
};

const styles = {
  iconAction: {
    placeSelf: "center",
    cursor: "pointer",
    justifySelf: "left",
    marginTop: "25px",
  },
};

const strings = new LocalizedStrings({
  es: {
    title: "Calibración",
    responses: {
      errorFetch: "Error al consultar dispositivo",
      errorFetchVariables: "Error al consultar variables",
      errorSave: "Error al guardar datos",
      endMustBeMajor: "El valor final debe ser igual o mayor al valor inicial",
      fieldsMissing: "Faltan campos por rellenar",
      successUpdated: "Calibración actualizada con éxito",
    },
    fields: {
      variable: "Variable",
      start: "Valor inicial",
      end: "Valor final",
      factor: "Factor de calibración",
    },
    save: "Guardar",
  },
});

export default DeviceCalibration;
